import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CourseDetailsDTO,
  CourseEnrollmentRequestDTO,
  CourseEnrollmentResponseDTO,
  CourseInstanceDetailResponseDTO,
  CourseInstancesResponseDTO,
  CourseSendReminderRequestDTO,
  CourseSendReminderResponseDTO,
  CourseUnenrollmentRequestDTO,
  CourseUnenrollmentResponseDTO,
} from '../models/CourseDTO';
import {
  toCourseEnrollmentRequestDTO,
  toCourseUnenrollmentRequestDTO,
  toSendReminderRequestDTO,
} from '../mappers/dto-mappers';
import { CurrentUserState } from '../../../../core/store/current-user/current-user.state';
import { PortalApiRepository } from '../../../../core/repositories/portal-api-repository';
import { HttpParams } from '@angular/common/http';
import { CourseEnrolledStudentUIModel } from '../models/CourseEnrolledStudentUIModel';

@Injectable({
  providedIn: 'root',
})
export class CourseService extends PortalApiRepository {
  constructor() {
    super();
  }

  getCourseInstances(): Observable<CourseInstancesResponseDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    if (!subscriptionId) {
      return EMPTY;
    }

    const path = `/subscriptions/${subscriptionId}/CourseInstances`;

    return this.get<CourseInstancesResponseDTO>(path);
  }

  getCourseDetail(id: string): Observable<CourseInstanceDetailResponseDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    if (!subscriptionId) {
      return EMPTY;
    }

    const path = `/subscriptions/${subscriptionId}/CourseInstances/${id}`;

    return this.get<CourseInstanceDetailResponseDTO>(path);
  }

  enrollStudentsToCourse(
    students: Array<{ id: string }>,
    courseInstanceId: string,
  ): Observable<CourseEnrollmentResponseDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/CourseInstances/${courseInstanceId}/enroll`;
    const payload = toCourseEnrollmentRequestDTO(students);

    return this.post<CourseEnrollmentRequestDTO, CourseEnrollmentResponseDTO>(
      path,
      payload,
    );
  }

  unenrollStudentsFromCourse(
    students: CourseEnrolledStudentUIModel[],
    courseInstanceId: string,
  ): Observable<CourseUnenrollmentResponseDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/CourseInstances/${courseInstanceId}/unenroll`;
    const payload = toCourseUnenrollmentRequestDTO(students);

    return this.post<
      CourseUnenrollmentRequestDTO,
      CourseUnenrollmentResponseDTO
    >(path, payload);
  }

  sendReminderToStudentsFromCourse(
    students: CourseEnrolledStudentUIModel[],
    courseInstanceId: string,
  ): Observable<CourseSendReminderResponseDTO> {
    const path = `/send-reminder/manual`;
    const payload = toSendReminderRequestDTO(students, courseInstanceId);

    return this.post<
      CourseSendReminderRequestDTO,
      CourseSendReminderResponseDTO
    >(path, payload);
  }

  exportStudentInfoToCsv(courseInstanceId: string): Observable<Blob> {
    const path = '/CourseUserActivityHistory/History';
    const subscription = this.store.selectSnapshot(
      CurrentUserState.subscription,
    );

    if (!subscription) {
      console.error("Subscription isn't set");
      return EMPTY;
    }

    const queryParams = new HttpParams()
      .set('startDate', subscription.currentPeriodStartedAt)
      .set('endDate', subscription.currentPeriodEndsAt)
      .set('courseInstanceId', courseInstanceId);

    return this.download(path, queryParams);
  }

  initCoursePreview() {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    if (!subscriptionId) {
      return EMPTY;
    }

    const path = `/subscriptions/${subscriptionId}/courses/InitCoursePreview`;
    return this.get(path, undefined, true);
  }

  getCoursesOfSubscription(): Observable<CourseDetailsDTO[]> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    if (!subscriptionId) {
      return EMPTY;
    }

    const path = `/subscriptions/${subscriptionId}/courses`;

    return this.get<CourseDetailsDTO[]>(path).pipe(
      map((courses) =>
        courses.map((course) => Object.assign(new CourseDetailsDTO(), course)),
      ),
    );
  }

  getCourseById(courseId: string): Observable<CourseDetailsDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    if (!subscriptionId) {
      return EMPTY;
    }

    const path = `/subscriptions/${subscriptionId}/courses/${courseId}`;

    return this.get<CourseDetailsDTO>(path).pipe(
      map((course) => Object.assign(new CourseDetailsDTO(), course)),
    );
  }
}
